var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/a-user.ts
import { Arg, Entity, Field, Mutation, Query } from "@adornis/baseql/decorators";
import { AdornisEntity, constructValue } from "@adornis/baseql/entities/adornisEntity";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity";
import { metaDataStore } from "@adornis/baseql/metaDataStore";
import OptimisticObservable from "@adornis/baseql/optimisticObservable";
import { getCollection } from "@adornis/baseql/server/collections";
import { context } from "@adornis/baseql/server/context";
import { selectionSet } from "@adornis/baseql/utils/queryGeneration";
import { validate } from "@adornis/validation/decorators";
import { and } from "@adornis/validation/functions/and";
import { nonOptional } from "@adornis/validation/functions/nonOptional";
import { BehaviorSubject, combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import {
  generatePasswordChangeToken,
  changePasswordWithToken,
  validateToken,
  useToken,
  getUserFromToken,
  hashPassword
} from "../server/a-userSecrets";
import { AdornisRoles } from "./a-roles";
import { TokenType } from "./tokenTypes";
import { AdornisRoleToContextsWrapper, NativePermissions } from "./types";
import {
  emailRegexCheck,
  emailValidation,
  nameRegexCheck,
  nameValidation,
  passwordValidation,
  validatePassword
} from "./validators";
var upgradeToUserContext = async (baseContext) => {
  baseContext.user = async () => baseContext.userID ? AdornisUser.getByID(baseContext.userID)(AdornisUser.allFields) : void 0;
};
var serializeUserContext = (baseContext) => ({
  loginToken: baseContext.loginToken,
  userID: baseContext.userID,
  impersonatingUser: baseContext.impersonatingUser
});
var BaseUser = class extends AdornisEntity {
};
BaseUser._class = "BaseUser";
__decorateClass([
  validate(and(nonOptional(), nameValidation)),
  Field((type) => String)
], BaseUser.prototype, "username", 2);
__decorateClass([
  validate(emailValidation),
  Field((type) => String)
], BaseUser.prototype, "email", 2);
__decorateClass([
  validate(passwordValidation),
  Field((type) => String)
], BaseUser.prototype, "password", 2);
BaseUser = __decorateClass([
  Entity()
], BaseUser);
var AdornisUser = class extends MongoEntity {
  constructor(doc) {
    super(doc);
  }
  static get allFields() {
    return selectionSet(() => this, 2);
  }
  static get collection() {
    return getCollection(AdornisUser._collectionName);
  }
  static get currentUser() {
    if (!this._currentUser)
      this._currentUser = new OptimisticObservable(
        combineLatest([this.optimisticUser, this.optimisticUserState]).pipe(
          map(([user, state]) => ({ fullyLoaded: state, data: user }))
        ),
        { fullyLoaded: false, data: void 0 }
      );
    return this._currentUser;
  }
  static updateCurrentUser(user, state) {
    this.optimisticUser.next(user);
    this.optimisticUserState.next(state);
  }
  static async login(usernameOrMail, password, loginTTL) {
    if (!nameRegexCheck(usernameOrMail) && !emailRegexCheck(usernameOrMail))
      throw new Error("Error in login(): Provided usernameOrMail is no valid name nor email");
    if (!password)
      throw new Error("No password given");
    const userID = await this.loginWithPassword(usernameOrMail, password)(this.allFields);
    this.updateCurrentUser(void 0, false);
    this.subscribeCurrentUser(userID);
  }
  static async subscribeCurrentUser(userID) {
    this.userSubcription?.unsubscribe();
    let impersonatingUserID = null;
    if (!userID)
      [userID, impersonatingUserID] = await this.getCurrentUserID()("");
    if (!userID) {
      this.impersonationStatus.next(null);
      this.updateCurrentUser(null, true);
      return;
    }
    this.userSubcription = this.subscribeByID(
      impersonatingUserID ? [userID, impersonatingUserID] : [userID]
    )(this.allFields).subscribe({
      next: (users) => {
        const user = users.find((u) => u._id === userID);
        const impersonatingUser = users.find((u) => u._id === impersonatingUserID);
        this.impersonationStatus.next(impersonatingUser);
        this.updateCurrentUser(user, true);
      },
      complete: () => {
        this.updateCurrentUser(null, true);
      }
    });
  }
  static async logout() {
    if (!await AdornisUser.currentUser.result)
      throw new Error("logout(): You are not logged in");
    this.userSubcription?.unsubscribe();
    this.updateCurrentUser(null, true);
    this.impersonationStatus.next(null);
    await this.downGradeSession()("");
  }
  static async registerUser(username, email = "", password, additionalFields) {
    this.passwordValidator(password);
    const user = new this({
      username,
      email,
      ...additionalFields
    });
    const id = await this.requestRegisterUser(user, password)("");
    return id;
  }
  static async impersonate(userId) {
    await this.impersonateUser(userId)("");
    this.subscribeCurrentUser();
  }
  static async stopImpersonation() {
    await this.stopImpersonationOfUser()("");
    this.subscribeCurrentUser();
  }
  static async changePassword(userId, password) {
    this.passwordValidator(password);
    const permissionToken = await this.generatePasswordChangeToken(userId)("");
    if (!permissionToken || !permissionToken.length)
      throw new Error("changePassword(): Permission denied");
    const result = await this.changePasswordWithToken(permissionToken, password)("");
    if (!result)
      throw new Error("changePassword(): resolving the permissionToken failed.");
  }
  static async assignRole(userId, roleContext) {
    let result = false;
    try {
      result = await AdornisUser.addRoleToUser(userId, roleContext)("");
    } catch (e) {
      console.error(e);
    }
    return result;
  }
  static async removeRole(userId, roleName) {
    let result = false;
    try {
      result = await AdornisUser.removeRoleFromUser(userId, roleName)("");
    } catch (e) {
      console.error(e);
    }
    return result;
  }
  hasPermission(permission, context2) {
    return AdornisRoles.optimisticallyCheckPermission(this, permission, context2);
  }
  static loginWithToken(token) {
    return async (gqlFields) => {
      const decryptedToken = await useToken(token, TokenType.LOGIN);
      const user = await getUserFromToken(decryptedToken);
      if (!context.serverContext) {
        context.userID = user._id;
        await context.recreate();
      }
      return user._id;
    };
  }
  static changePasswordWithToken(token, newPassword) {
    return async (gqlFields) => {
      this.passwordValidator(newPassword);
      return changePasswordWithToken(token, newPassword);
    };
  }
  static checkPasswordChangeToken(token) {
    return async (gqlFields) => {
      const result = await validateToken(token, TokenType.PASSWORD_CHANGE);
      return !!result;
    };
  }
  static getUserByUsernameOrEmail(usernameOrEmail, forceType) {
    return async (gqlFields) => {
      const collection = await getCollection(this._collectionName);
      const selector = { $regex: `^${usernameOrEmail.replace(/([^\w]{1})/g, "\\$1")}$`, $options: "i" };
      return collection.findOne(
        forceType === "email" ? { email: selector } : forceType === "username" ? { username: selector } : { $or: [{ email: selector }, { username: selector }] },
        { projection: { password: 0 } }
      );
    };
  }
  static loginWithPassword(usernameOrMail, password) {
    return async (gqlFields) => {
      const collection = await getCollection(AdornisUser._collectionName);
      let user;
      const passHash = hashPassword(password);
      if (nameRegexCheck(usernameOrMail) || emailRegexCheck(usernameOrMail)) {
        user = await collection.findOne(
          {
            $or: [
              { username: { $regex: `^${usernameOrMail.replace(/([^\w]{1})/g, "\\$1")}$`, $options: "i" } },
              { email: { $regex: `^${usernameOrMail.replace(/([^\w]{1})/g, "\\$1")}$`, $options: "i" } }
            ],
            password: passHash
          },
          { projection: { _id: 1 } }
        );
      } else
        throw new Error("loginWithPassword(): Provided usernameOrMail is neither an username nor email");
      if (!user)
        throw new Error("loginWithPassword(): Username/Email or password is wrong");
      if (!context.serverContext) {
        context.userID = user._id;
        await context.recreate();
      }
      return user._id;
    };
  }
  static downGradeSession() {
    return async (gqlFields) => {
      if (!context.serverContext) {
        context.userID = void 0;
        context.impersonatingUser = void 0;
        await context.recreate();
      }
      return true;
    };
  }
  static getCurrentUserID() {
    return async (gqlFields) => {
      return context.serverContext ? ["", null] : [context.userID, context.impersonatingUser];
    };
  }
  static impersonateUser(userID) {
    return async (gqlFields) => {
      if (context.serverContext)
        throw new Error("impersonateUser(): The server does not need to impersonate someone");
      if (!context.userID)
        throw new Error("impersonateUser(): Not logged in, permission denied.");
      const collection = await getCollection(AdornisUser._collectionName);
      const requestingUser = await collection.findOne(
        { _id: context.userID },
        { projection: { _id: 1, nativePermissions: 1 } }
      );
      if (!requestingUser)
        throw new Error("impersonateUser(): Invalid loginToken, permission denied.");
      const victim = await collection.findOne({ _id: userID }, { projection: { _id: 1, nativePermissions: 1 } });
      if (!victim)
        throw new Error("impersonateUser(): userId is invalid, permission denied");
      if (this.useNativePermissions && victim.nativePermissions.impersonationLevel >= requestingUser.nativePermissions.impersonationLevel)
        throw new Error("impersonateUser(): You dont have the permission to impersonate this user.");
      context.impersonatingUser = context.userID;
      context.userID = victim._id;
      await context.recreate();
      return victim._id;
    };
  }
  static stopImpersonationOfUser() {
    return async (gqlFields) => {
      if (!context.serverContext) {
        if (!context.impersonatingUser)
          return;
        context.userID = context.impersonatingUser;
        context.impersonatingUser = void 0;
        await context.recreate();
      }
      return true;
    };
  }
  static generatePasswordChangeToken(userID) {
    return async (gqlFields) => {
      if (context.serverContext)
        throw new Error("generatePasswordChangeToken(): The server does not need to request a password change token");
      if (!context.userID)
        throw new Error("generatePasswordChangeToken(): Not logged in, permission denied.");
      const collection = await getCollection(AdornisUser._collectionName);
      const requestingUser = await collection.findOne(
        { _id: context.userID },
        { projection: { _id: 1, nativePermissions: 1 } }
      );
      if (!requestingUser)
        throw new Error("generatePasswordChangeToken(): Invalid loginToken, permission denied.");
      const victim = await collection.findOne({ _id: userID }, { projection: { _id: 1, nativePermissions: 1 } });
      if (!victim)
        throw new Error("generatePasswordChangeToken(): userId is invalid, permission denied");
      if (this.useNativePermissions && victim.nativePermissions.passwordChangeLevel >= requestingUser.nativePermissions.passwordChangeLevel && requestingUser._id !== victim._id)
        throw new Error(
          "generatePasswordChangeToken(): You dont have the permission to change the password of this user."
        );
      return generatePasswordChangeToken(victim._id);
    };
  }
  static requestRegisterUser(userObj, password) {
    return async (gqlFields) => {
      if (!this.validSubclasses(metaDataStore.find((node) => node.type() === AdornisUser)).includes(userObj._class))
        throw new Error("userObj doesnt extend AdornisUser");
      this.passwordValidator(password);
      const collection = await getCollection(AdornisUser._collectionName);
      let user;
      user = await collection.findOne(
        { email: { $regex: `^${userObj.email.replace(/([^\w]{1})/g, "\\$1")}$`, $options: "i" } },
        { projection: { password: 0 } }
      );
      if (user && user._id)
        throw new Error("requestRegisterUser(): Email is already in use");
      user = await collection.findOne(
        { username: { $regex: `^${userObj.username.replace(/([^\w]{1})/g, "\\$1")}$`, $options: "i" } },
        { projection: { password: 0 } }
      );
      if (user && user._id)
        throw new Error("requestRegisterUser(): Username is already in use");
      const id = await AdornisUser.createEntity(userObj._class, userObj)("");
      await collection.updateOne({ _id: id }, { $set: { password: hashPassword(password) } });
      return id;
    };
  }
  static addRoleToUser(userId, roleContextMap) {
    return async (gqlFields) => {
      if (!userId)
        throw new Error("You need to pass a valid userId");
      const user = await AdornisUser.getByID(userId)(`{ _id _class roles ${AdornisRoles.allFields} }`);
      if (!user)
        throw new Error("No user found for given id");
      if (user.roles.map((role) => role.name).includes(roleContextMap.name))
        return true;
      else
        user.roles.push(roleContextMap);
      await user.save();
      return true;
    };
  }
  static removeRoleFromUser(userId, roleName) {
    return async (gqlFields, context2) => {
      if (!userId)
        throw new Error("You need to pass a valid userId");
      const user = await AdornisUser.getByID(userId)(`{ _id _class roles ${AdornisRoles.allFields} }`);
      if (!user)
        throw new Error("No user found for given id");
      const index = user.roles.map((r) => r.name).findIndex((r) => r === roleName);
      if (index === -1)
        return true;
      user.roles.splice(index, 1);
      await user.save();
      return true;
    };
  }
  static isPasswordSet(userId) {
    return async (gqlFields) => {
      const collection = await this.collection;
      const result = await collection.findOne({ _id: userId, password: { $exists: true } });
      if (!result)
        return false;
      return true;
    };
  }
};
AdornisUser._collectionName = "adornisuser";
AdornisUser._class = "AdornisUser";
AdornisUser.impersonationStatus = new BehaviorSubject(void 0);
AdornisUser.optimisticUser = new BehaviorSubject(null);
AdornisUser.optimisticUserState = new BehaviorSubject(false);
AdornisUser.userSubcription = void 0;
AdornisUser.useNativePermissions = true;
AdornisUser.usernameValidator = and(nonOptional(), nameValidation);
AdornisUser.emailValidator = emailValidation;
AdornisUser.passwordValidator = validatePassword;
__decorateClass([
  validate(
    (value, fieldName, entity) => constructValue(entity).constructor.usernameValidator(value, fieldName, entity)
  ),
  Field((type) => String)
], AdornisUser.prototype, "username", 2);
__decorateClass([
  validate(
    (value, fieldName, entity) => constructValue(entity).constructor.emailValidator(value, fieldName, entity)
  ),
  Field((type) => String)
], AdornisUser.prototype, "email", 2);
__decorateClass([
  Field((type) => [AdornisRoleToContextsWrapper], { default: (v) => v ?? [] })
], AdornisUser.prototype, "roles", 2);
__decorateClass([
  Field((type) => Boolean, { default: (v) => v ?? false })
], AdornisUser.prototype, "isOnline", 2);
__decorateClass([
  Field((type) => Date, { default: (v) => v ?? new Date() })
], AdornisUser.prototype, "createdAt", 2);
__decorateClass([
  Field((type) => NativePermissions, { default: (v) => v ?? new NativePermissions({}) })
], AdornisUser.prototype, "nativePermissions", 2);
__decorateClass([
  Field((type) => String, { default: (x) => x ?? "" })
], AdornisUser.prototype, "ios_sub", 2);
__decorateClass([
  Field((type) => Boolean, { default: (v) => v ?? false })
], AdornisUser.prototype, "emailConfirmed", 2);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("token", (type) => String))
], AdornisUser, "loginWithToken", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("token", (type) => String)),
  __decorateParam(1, Arg("newPassword", (type) => String))
], AdornisUser, "changePasswordWithToken", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("token", (type) => String))
], AdornisUser, "checkPasswordChangeToken", 1);
__decorateClass([
  Query((type) => AdornisUser),
  __decorateParam(0, Arg("usernameOrEmail", (type) => String)),
  __decorateParam(1, Arg("forceType", (type) => String))
], AdornisUser, "getUserByUsernameOrEmail", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("usernameOrMail", (type) => String)),
  __decorateParam(1, Arg("password", (type) => String))
], AdornisUser, "loginWithPassword", 1);
__decorateClass([
  Mutation((type) => Boolean)
], AdornisUser, "downGradeSession", 1);
__decorateClass([
  Query((type) => [String], { requestHandlerMeta: { queryCache: { cachePolicy: "bypass" } } })
], AdornisUser, "getCurrentUserID", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("userId", (type) => String))
], AdornisUser, "impersonateUser", 1);
__decorateClass([
  Mutation((type) => Boolean)
], AdornisUser, "stopImpersonationOfUser", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("userId", (type) => String))
], AdornisUser, "generatePasswordChangeToken", 1);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("userObj", (type) => MongoEntity)),
  __decorateParam(1, Arg("password", (type) => String))
], AdornisUser, "requestRegisterUser", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("userId", (type) => String)),
  __decorateParam(1, Arg("roleContextMap", (type) => AdornisRoleToContextsWrapper))
], AdornisUser, "addRoleToUser", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("userId", (type) => String)),
  __decorateParam(1, Arg("roleName", (type) => String))
], AdornisUser, "removeRoleFromUser", 1);
__decorateClass([
  Query((type) => Boolean),
  __decorateParam(0, Arg("userId", (type) => String))
], AdornisUser, "isPasswordSet", 1);
AdornisUser = __decorateClass([
  Entity()
], AdornisUser);
export {
  AdornisUser,
  BaseUser,
  serializeUserContext,
  upgradeToUserContext
};
